import {
  AlertCircle,
  CheckCircle,
  Clock,
  Download,
  Eye,
  Mail,
  Search,
  Send,
  Trash2,
  Upload,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useLanguage } from "../../context/LanguageContext";
import { adeverintaAPI } from "../../lib/api";
import AdminLayout from "./AdminLayout";

const AdminAdeverintaPage = () => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [uploadModal, setUploadModal] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [statusModal, setStatusModal] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deletingBatch, setDeletingBatch] = useState(false);
  const [deleteBatchModal, setDeleteBatchModal] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const status = statusFilter === "all" ? null : statusFilter;
      const data = await adeverintaAPI.getAll(status);
      setItems(data);
    } catch (error) {
      toast.error("Failed to fetch certificates");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      setSelectedFile(file);
    } else {
      toast.error(
        lang === "en" ? "Please select a PDF file" : "Selectați un fișier PDF",
      );
    }
  };

  const handleUploadSigned = async () => {
    if (!selectedFile || !uploadModal) return;

    setUploading(true);
    try {
      await adeverintaAPI.uploadSigned(uploadModal.id, selectedFile);
      toast.success(
        lang === "en"
          ? `Signed PDF sent to ${uploadModal.email}`
          : lang === "ar"
          ? `تم إرسال PDF الموقع إلى ${uploadModal.email}`
          : `PDF semnat trimis la ${uploadModal.email}`,
      );
      setUploadModal(null);
      setSelectedFile(null);
      fetchItems();
    } catch (error) {
      toast.error(
        lang === "en"
          ? "Failed to upload signed PDF"
          : lang === "ar"
          ? "خطأ في رفع PDF الموقع"
          : "Eroare la încărcarea PDF-ului",
      );
    } finally {
      setUploading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!statusModal || !selectedStatus) return;

    setUpdatingStatus(true);
    try {
      await adeverintaAPI.updateStatus(statusModal.id, selectedStatus);
      toast.success(
        lang === "en"
          ? "Status updated successfully"
          : "Statut actualizat cu succes",
      );
      setStatusModal(null);
      setSelectedStatus("");
      fetchItems();
    } catch (error) {
      toast.error(
        lang === "en"
          ? "Failed to update status"
          : "Eroare la actualizarea statutului",
      );
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;

    setDeleting(true);
    try {
      await adeverintaAPI.delete(deleteModal.id);
      toast.success(
        lang === "en"
          ? "Certificate deleted successfully"
          : "Certificat șters cu succes",
      );
      setDeleteModal(null);
      fetchItems();
    } catch (error) {
      toast.error(
        lang === "en"
          ? "Failed to delete certificate"
          : "Eroare la ștergerea certificatului",
      );
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelection = (id) => {
    const newSelectedIds = new Set(selectedIds);
    if (newSelectedIds.has(id)) {
      newSelectedIds.delete(id);
    } else {
      newSelectedIds.add(id);
    }
    setSelectedIds(newSelectedIds);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredItems.length && selectedIds.size > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredItems.map((item) => item.id)));
    }
  };

  const handleDeleteBatch = async () => {
    if (selectedIds.size === 0) return;
    setDeleteBatchModal(true);
  };

  const handleExecuteDeleteBatch = async () => {
    if (selectedIds.size === 0) return;

    setDeletingBatch(true);
    try {
      let successCount = 0;
      for (const id of selectedIds) {
        try {
          await adeverintaAPI.delete(id);
          successCount++;
        } catch (error) {
          console.error(`Failed to delete ${id}`, error);
        }
      }

      toast.success(
        lang === "en"
          ? `${successCount} certificate(s) deleted successfully`
          : `${successCount} certificat(e) șters cu succes`,
      );
      setSelectedIds(new Set());
      setDeleteBatchModal(false);
      fetchItems();
    } catch (error) {
      toast.error(
        lang === "en"
          ? "Failed to delete certificates"
          : "Eroare la ștergerea certificatelor",
      );
    } finally {
      setDeletingBatch(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === "en" ? "en-GB" : "ro-RO", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded">
            <AlertCircle className="w-3 h-3" />
            {t("admin.status.pending")}
          </span>
        );
      case "signed":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 text-xs rounded">
            <CheckCircle className="w-3 h-3" />
            {t("admin.status.signed")}
          </span>
        );
      case "sent":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
            <Mail className="w-3 h-3" />
            {t("admin.status.sent")}
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 bg-stone-100 text-stone-600 text-xs rounded">
            {status}
          </span>
        );
    }
  };

  const filteredItems = items.filter((item) => {
    const fullName = `${item.nume} ${item.prenume}`.toLowerCase();
    const email = item.email.toLowerCase();
    const cnp = item.cnp;
    const search = searchTerm.toLowerCase();
    return (
      fullName.includes(search) ||
      email.includes(search) ||
      cnp.includes(search)
    );
  });

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-stone-800">
            {t("admin.certificates")}
          </h1>
          <p className="text-stone-500 text-sm">
            {items.length} total •{" "}
            {items.filter((i) => i.status === "pending").length} pending
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-grow max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            type="text"
            placeholder={t("common.search") + " (name, email, CNP)..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-certificates"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">{t("admin.status.pending")}</SelectItem>
            <SelectItem value="signed">{t("admin.status.signed")}</SelectItem>
            <SelectItem value="sent">{t("admin.status.sent")}</SelectItem>
          </SelectContent>
        </Select>
        {selectedIds.size > 0 && (
          <Button
            onClick={handleDeleteBatch}
            disabled={deletingBatch}
            className="bg-red-600 hover:bg-red-700 text-white gap-2"
            data-testid="delete-selected">
            <Trash2 className="w-4 h-4" />
            {deletingBatch ? (
              <>{t("common.loading")}</>
            ) : (
              <>
                {lang === "en"
                  ? `Delete (${selectedIds.size})`
                  : lang === "ar"
                  ? `حذف (${selectedIds.size})`
                  : `Șterge (${selectedIds.size})`}
              </>
            )}
          </Button>
        )}
      </div>

      {/* Table */}
      <Card className="border-stone-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8">
              <div className="animate-pulse space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-16 bg-stone-200 rounded"></div>
                ))}
              </div>
            </div>
          ) : filteredItems.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200 bg-stone-50">
                    <th className="text-center text-xs font-medium text-stone-500 uppercase px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={
                          selectedIds.size > 0 &&
                          selectedIds.size === filteredItems.length
                        }
                        onChange={toggleSelectAll}
                        className="w-4 h-4 cursor-pointer"
                        data-testid="select-all"
                      />
                    </th>
                    <th className="text-left text-xs font-medium text-stone-500 uppercase px-4 py-3">
                      Name
                    </th>
                    <th className="text-left text-xs font-medium text-stone-500 uppercase px-4 py-3 hidden md:table-cell">
                      CNP
                    </th>
                    <th className="text-left text-xs font-medium text-stone-500 uppercase px-4 py-3 hidden lg:table-cell">
                      Email
                    </th>
                    <th className="text-left text-xs font-medium text-stone-500 uppercase px-4 py-3 hidden sm:table-cell">
                      Status
                    </th>
                    <th className="text-left text-xs font-medium text-stone-500 uppercase px-4 py-3 hidden xl:table-cell">
                      Date
                    </th>
                    <th className="text-right text-xs font-medium text-stone-500 uppercase px-4 py-3">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filteredItems.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-stone-50 cursor-pointer"
                      data-testid={`adeverinta-row-${item.id}`}
                      onClick={() => navigate(`/admin/adeverinta/${item.id}`)}>
                      <td
                        className="px-4 py-4 text-center w-10"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSelection(item.id);
                        }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item.id)}
                          onChange={() => toggleSelection(item.id)}
                          className="w-4 h-4 cursor-pointer"
                          data-testid={`select-${item.id}`}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-medium text-stone-800">
                            {item.nume} {item.prenume}
                          </p>
                          <p className="text-xs text-stone-500 mt-1">
                            {item.employer_name}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <span className="font-mono text-sm text-stone-600">
                          {item.cnp}
                        </span>
                      </td>
                      <td className="px-4 py-4 hidden lg:table-cell">
                        <span className="text-sm text-stone-600">
                          {item.email}
                        </span>
                      </td>
                      <td className="px-4 py-4 hidden sm:table-cell">
                        {getStatusBadge(item.status)}
                      </td>
                      <td className="px-4 py-4 hidden xl:table-cell">
                        <span className="text-sm text-stone-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(item.created_at)}
                        </span>
                      </td>
                      <td
                        className="px-4 py-4 text-right"
                        onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          {item.pdf_url && (
                            <a
                              href={item.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer">
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Download PDF">
                                <Download className="w-4 h-4" />
                              </Button>
                            </a>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setStatusModal(item);
                              setSelectedStatus(item.status);
                            }}
                            className="gap-1"
                            data-testid={`change-status-${item.id}`}
                            title={
                              lang === "en" ? "Change Status" : "Schimbă statut"
                            }>
                            {lang === "en"
                              ? "Change Status"
                              : lang === "ar"
                              ? "تغيير الحالة"
                              : "Schimbă"}
                          </Button>
                          {item.status === "pending" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setUploadModal(item)}
                              className="gap-1"
                              data-testid={`upload-signed-${item.id}`}>
                              <Upload className="w-3 h-3" />
                              {lang === "en"
                                ? "Upload Signed"
                                : lang === "ar"
                                ? "رفع الموقع"
                                : "Încarcă semnat"}
                            </Button>
                          )}
                          {item.signed_pdf_url && (
                            <a
                              href={item.signed_pdf_url}
                              target="_blank"
                              rel="noopener noreferrer">
                              <Button
                                variant="ghost"
                                size="icon"
                                title="View Signed PDF">
                                <Eye className="w-4 h-4 text-teal-600" />
                              </Button>
                            </a>
                          )}
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setDeleteModal(item)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title={
                              lang === "en"
                                ? "Delete Certificate"
                                : "Șterge certificat"
                            }
                            data-testid={`delete-${item.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <p className="text-stone-500">{t("common.noResults")}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Modal */}
      <Dialog
        open={!!uploadModal}
        onOpenChange={() => {
          setUploadModal(null);
          setSelectedFile(null);
        }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {lang === "en"
                ? "Upload Signed PDF"
                : lang === "ar"
                ? "رفع PDF الموقع"
                : "Încarcă PDF semnat"}
            </DialogTitle>
            <DialogDescription>
              {uploadModal && (
                <span>
                  {lang === "en" ? "For" : lang === "ar" ? "إلى" : "Pentru"}:{" "}
                  {uploadModal.nume} {uploadModal.prenume}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>
                {lang === "en"
                  ? "Select PDF file"
                  : lang === "ar"
                  ? "اختر ملف PDF"
                  : "Selectează fișierul PDF"}
              </Label>
              <div className="mt-2">
                <label
                  className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    selectedFile
                      ? "border-islamic-teal bg-islamic-teal/5"
                      : "border-stone-300 hover:border-islamic-teal hover:bg-stone-50"
                  }`}>
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    {selectedFile ? (
                      <>
                        <CheckCircle className="w-8 h-8 text-islamic-teal mb-2" />
                        <p className="text-sm text-islamic-teal font-medium">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-stone-500 mt-1">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-stone-400 mb-2" />
                        <p className="text-sm text-stone-500">
                          {lang === "en"
                            ? "Click to select PDF"
                            : lang === "ar"
                            ? "انقر لاختيار PDF"
                            : "Click pentru a selecta PDF"}
                        </p>
                        <p className="text-xs text-stone-400">PDF, max 10MB</p>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,application/pdf"
                    onChange={handleFileSelect}
                    data-testid="file-input"
                  />
                </label>
              </div>
            </div>

            {uploadModal && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <Send className="w-4 h-4 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">
                      {lang === "en"
                        ? "Email will be sent to:"
                        : lang === "ar"
                        ? "سيتم إرسال البريد الإلكتروني إلى:"
                        : "Email-ul va fi trimis la:"}
                    </p>
                    <p>{uploadModal.email}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setUploadModal(null);
                setSelectedFile(null);
              }}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleUploadSigned}
              disabled={!selectedFile || uploading}
              className="bg-islamic-teal hover:bg-islamic-teal-light gap-2"
              data-testid="confirm-upload">
              {uploading ? (
                <>{t("common.loading")}</>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  {lang === "en"
                    ? "Upload & Send"
                    : lang === "ar"
                    ? "رفع وإرسال"
                    : "Încarcă și trimite"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Modal */}
      <Dialog
        open={!!statusModal}
        onOpenChange={() => {
          setStatusModal(null);
          setSelectedStatus("");
        }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {lang === "en"
                ? "Change Certificate Status"
                : lang === "ar"
                ? "تغيير حالة الشهادة"
                : "Schimbă statutul certificatului"}
            </DialogTitle>
            <DialogDescription>
              {statusModal && (
                <span>
                  {lang === "en" ? "For" : lang === "ar" ? "إلى" : "Pentru"}:{" "}
                  {statusModal.nume} {statusModal.prenume}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label
                htmlFor="status-select"
                className="text-base font-medium mb-2 block">
                {lang === "en"
                  ? "Select New Status"
                  : lang === "ar"
                  ? "حدد الحالة الجديدة"
                  : "Selectează noul statut"}
              </Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger id="status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">
                    {t("admin.status.pending")}
                  </SelectItem>
                  <SelectItem value="signed">
                    {t("admin.status.signed")}
                  </SelectItem>
                  <SelectItem value="sent">{t("admin.status.sent")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {statusModal && selectedStatus && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">
                      {lang === "en"
                        ? "Current Status"
                        : lang === "ar"
                        ? "الحالة الحالية"
                        : "Statut curent"}
                      : {getStatusBadge(statusModal.status)}
                    </p>
                    <p className="mt-1">
                      {lang === "en"
                        ? "New Status"
                        : lang === "ar"
                        ? "الحالة الجديدة"
                        : "Noul statut"}
                      : {getStatusBadge(selectedStatus)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setStatusModal(null);
                setSelectedStatus("");
              }}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleStatusChange}
              disabled={
                !selectedStatus ||
                updatingStatus ||
                selectedStatus === statusModal?.status
              }
              className="bg-islamic-teal hover:bg-islamic-teal-light"
              data-testid="confirm-status-change">
              {updatingStatus ? (
                <>{t("common.loading")}</>
              ) : (
                <>
                  {lang === "en"
                    ? "Update Status"
                    : lang === "ar"
                    ? "تحديث الحالة"
                    : "Actualizează statut"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={!!deleteModal} onOpenChange={() => setDeleteModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {lang === "en"
                ? "Delete Certificate"
                : lang === "ar"
                ? "حذف الشهادة"
                : "Șterge certificat"}
            </DialogTitle>
            <DialogDescription>
              {deleteModal && (
                <span>
                  {lang === "en"
                    ? "This will permanently delete the certificate for"
                    : lang === "ar"
                    ? "سيؤدي هذا إلى حذف الشهادة الدائم"
                    : "Aceasta va șterge permanent certificatul pentru"}{" "}
                  <strong>
                    {deleteModal.nume} {deleteModal.prenume}
                  </strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {deleteModal && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-700">
                    <p className="font-medium mb-1">
                      {lang === "en"
                        ? "Warning"
                        : lang === "ar"
                        ? "تحذير"
                        : "Avertisment"}
                    </p>
                    <p>
                      {lang === "en"
                        ? "This action cannot be undone. The certificate and all its associated data will be permanently removed from the database."
                        : lang === "ar"
                        ? "لا يمكن التراجع عن هذا الإجراء. سيتم إزالة الشهادة وجميع بيانات الشهادة المرتبطة بها من قاعدة البيانات بشكل دائم"
                        : "Această acțiune nu poate fi anulată. Certificatul și toate datele sale asociate vor fi eliminate permanent din baza de date."}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="text-sm text-stone-600">
                  <strong>CNP:</strong> {deleteModal.cnp}
                </p>
                <p className="text-sm text-stone-600">
                  <strong>Email:</strong> {deleteModal.email}
                </p>
                <p className="text-sm text-stone-600">
                  <strong>
                    {lang === "en"
                      ? "Status"
                      : lang === "ar"
                      ? "الحالة"
                      : "Statut"}
                    :
                  </strong>{" "}
                  {getStatusBadge(deleteModal.status)}
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete">
              {deleting ? (
                <>{t("common.loading")}</>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  {lang === "en"
                    ? "Delete Certificate"
                    : lang === "ar"
                    ? "حذف الشهادة"
                    : "Șterge certificat"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Delete Modal */}
      <Dialog
        open={deleteBatchModal}
        onOpenChange={() => setDeleteBatchModal(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {lang === "en"
                ? `Delete ${selectedIds.size} Certificate(s)`
                : lang === "ar"
                ? `حذف ${selectedIds.size} الشهادة(الشهادات)`
                : `Șterge ${selectedIds.size} certificat(e)`}
            </DialogTitle>
            <DialogDescription>
              {lang === "en"
                ? "You are about to delete multiple certificates"
                : lang === "ar"
                ? "أنت على وشك حذف شهادات متعددة"
                : "Sunteți pe punctul de a șterge mai multe certificatele"}
            </DialogDescription>
          </DialogHeader>

          {selectedIds.size > 0 && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-700">
                    <p className="font-medium mb-2">
                      {lang === "en"
                        ? "Warning"
                        : lang === "ar"
                        ? "تحذير"
                        : "Avertisment"}
                    </p>
                    <p className="mb-3">
                      {lang === "en"
                        ? "This action cannot be undone. All selected certificates and their associated data will be permanently removed from the database."
                        : lang === "ar"
                        ? "لا يمكن التراجع عن هذا الإجراء. سيتم إزالة جميع الشهادات المحددة وجميع بيانات الشهادة المرتبطة بها من قاعدة البيانات بشكل دائم."
                        : "Această acțiune nu poate fi anulată. Toate certificatele selectate și toate datele lor asociate vor fi eliminate permanent din baza de date."}
                    </p>
                    <p className="text-xs font-semibold">
                      {lang === "en"
                        ? `You are about to delete ${selectedIds.size} certificate(s)`
                        : lang === "ar"
                        ? `أنت على وشك حذف ${selectedIds.size} الشهادة(الشهادات)`
                        : `Sunteți pe punctul de a șterge ${selectedIds.size} certificat(e)`}
                    </p>
                  </div>
                </div>
              </div>

              <div className="max-h-40 overflow-y-auto p-3 bg-stone-50 border border-stone-200 rounded-lg">
                <p className="text-xs font-semibold text-stone-600 mb-2">
                  {lang === "en"
                    ? "Selected certificates:"
                    : lang === "ar"
                    ? "الشهادات المحددة:"
                    : "Certificatele selectate:"}
                </p>
                <div className="space-y-1">
                  {items
                    .filter((item) => selectedIds.has(item.id))
                    .map((item) => (
                      <div key={item.id} className="text-xs text-stone-600">
                        <span className="font-medium">
                          {item.nume} {item.prenume}
                        </span>{" "}
                        - {item.email}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteBatchModal(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleExecuteDeleteBatch}
              disabled={deletingBatch}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-batch-delete">
              {deletingBatch ? (
                <>{t("common.loading")}</>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  {lang === "en"
                    ? `Delete ${selectedIds.size} Certificate(s)`
                    : lang === "ar"
                    ? `حذف ${selectedIds.size} الشهادة(الشهادات)`
                    : `Șterge ${selectedIds.size} certificat(e)`}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminAdeverintaPage;
